import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Alert,
  ButtonLink,
  Card,
  Eyebrow,
  Heading,
  Lead,
  Section,
  Spinner,
  cx,
} from '../components/ui';
import { api, formatPrice, type Plan } from '../lib/api';
import { SELLER_NOTICE } from '../lib/company';
import { usePageTitle } from '../lib/usePageTitle';

/**
 * Sign-up is live from this page again.
 *
 * It was paused - both "Get Started" buttons disabled behind a
 * `GET_STARTED_DISABLED` constant - because plan selection inside the dashboard
 * was paused too, and sending a visitor to a registration form for a plan they
 * then could not select is worse than not inviting them. That reasoning was
 * sound and no longer applies: the launch promotion means a new account can
 * pick any plan and run it, so the form leads somewhere.
 *
 * The pair is gone together, deliberately. Its dashboard half
 * (`PAID_PLAN_SELECTION_DISABLED` in `pages/app/Subscription.tsx`) was replaced
 * by `subscription.can_select_paid_plans`, computed on the server; leaving this
 * one behind would have put the two back out of step, which is the exact
 * failure the original comment warned about.
 */

/**
 * Copy that is not derivable from the API.
 *
 * Price, agent count and hours come from the live catalogue so this page can
 * never disagree with what the backend actually enforces; only the prose lives
 * here. Project and acceptance-criteria counts are advertised limits the API
 * does not meter yet, so they are written as copy rather than dressed up as
 * entitlements the platform serves.
 *
 * `projectSetup` is separate from `features` only so the bullets keep their
 * given order: agents, project setup, hours, then the rest.
 */
const PLAN_COPY: Record<Plan['plan'], { projectSetup: string; features: string[] }> = {
  free: {
    projectSetup: '1 Project Setup (localhost only)',
    features: [
      'Web page QA Harness',
      'Standard QA reports',
      'llm configuration',
      '200 Acceptance Criteria/month',
    ],
  },
  basic: {
    projectSetup: '2 Projects setup',
    features: [
      'Web page QA Harness',
      'Security Baseline Harness',
      'Standard QA reports',
      'llm configuration',
      '500 Acceptance Criteria/month',
    ],
  },
  standard: {
    projectSetup: '4 Projects setup',
    features: [
      'Web page QA Harness',
      'Security Baseline Harness',
      'Security and compliance QA',
      'Standard QA reports',
      'WaveQA self adaptation',
      'llm configuration',
      'limit llm calls',
      '1000 Acceptance Criteria/month',
      'Customer Support',
    ],
  },
  premium: {
    projectSetup: 'Unlimited Projects setup',
    features: [
      'Web page QA Harness',
      'Security Baseline Harness',
      'Security and compliance QA',
      'Separate QA reports',
      'WaveQA self adaptation',
      'llm configuration',
      'limit llm calls',
      'Unlimited Acceptance Criteria/month',
      'Customer Support',
    ],
  },
};

/**
 * Enterprise is an engagement, not a metered subscription: there is no plan row
 * behind it, so it is rendered from here rather than from the catalogue.
 */
const ENTERPRISE = {
  name: 'Enterprise',
  features: [
    'Develop QA Agents',
    'Architectural support',
    'Freelancing code development guidance',
    'Restructuring the business for AI driven development',
    'Team training',
    'Team guidance',
  ],
};

export function Pricing() {
  usePageTitle('Pricing — WaveQA');

  const { data: plans, isPending, isError } = useQuery({
    queryKey: ['plans'],
    queryFn: api.getPlans,
  });

  return (
    <>
      <Section className="border-b border-hull-800">
        <Eyebrow>Pricing</Eyebrow>
        <Heading level={1} className="text-white">
          Straightforward plans
        </Heading>
        <Lead className="mt-5 max-w-2xl">
          A subscription covers use of WaveQA&rsquo;s hosted services and API
          authorization. Professional setup and ongoing support are separate,
          optional services — described further down this page.
        </Lead>

        {/* --- SaaS subscriptions -------------------------------------- */}
        <div className="mt-12">
          <h2 className="mb-6 text-lg font-semibold text-white">
            WaveQA Agent subscriptions
          </h2>

          {isPending && (
            <div className="py-10">
              <Spinner label="Loading plans" />
            </div>
          )}

          {isError && (
            <Alert tone="error">
              Plans could not be loaded right now. They are served from the same
              catalogue the platform enforces, so rather than show figures that
              might be stale, this page shows nothing.
            </Alert>
          )}

          {plans && (
            <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {plans.map((plan) => (
                <PlanCard key={plan.plan} plan={plan} />
              ))}
            </ul>
          )}

          {/* Sits below the grid rather than in it: Enterprise is quoted work,
              not a metered subscription the platform can provision. */}
          <Card className="mt-4">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
              <h3 className="text-base font-semibold text-white">
                {ENTERPRISE.name}
              </h3>
              <p className="text-xl font-semibold text-white">Contact Sales</p>
            </div>
            <ul className="mt-5 grid gap-2 text-sm text-hull-300 sm:grid-cols-2 xl:grid-cols-3">
              {ENTERPRISE.features.map((feature) => (
                <li key={feature} className="flex gap-2">
                  <span aria-hidden="true" className="text-surge-400">
                    ✓
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <ButtonLink to="/contact" variant="secondary">
                Contact Sales
              </ButtonLink>
            </div>
          </Card>

          <p className="mt-5 text-xs leading-relaxed text-hull-400">
            Promotional and free-hour allowances are subject to the applicable
            terms. Exact definitions of metered hours are confirmed at signup.
          </p>

          {/* A buyer decides here, so the billing terms belong here too
              rather than only in a document they will not open. */}
          <div className="mt-8 rounded-card border border-hull-700 bg-hull-800/40 p-6">
            <h3 className="text-base font-semibold text-white">
              Billing, tax and refunds
            </h3>
            <p className="mt-2.5 max-w-3xl text-sm leading-relaxed text-hull-300">
              {SELLER_NOTICE}
            </p>
            <ul className="mt-5 grid gap-2.5 text-sm text-hull-300 sm:grid-cols-2">
              {[
                'Paid plans are billed monthly in advance and renew until you cancel.',
                'Any tax that applies is shown before you confirm payment, never added afterwards.',
                'Cancel at any time; your plan runs to the end of the period you paid for.',
                'Full refund within 14 days of your first payment on a paid plan.',
              ].map((item) => (
                <li key={item} className="flex gap-2.5">
                  <span aria-hidden="true" className="mt-px text-surge-400">
                    ·
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-hull-400">
              Full detail in the{' '}
              <Link
                to="/refund-policy"
                className="text-surge-400 underline underline-offset-2 hover:text-surge-300"
              >
                Refund Policy
              </Link>{' '}
              and the{' '}
              <Link
                to="/terms"
                className="text-surge-400 underline underline-offset-2 hover:text-surge-300"
              >
                Terms of Service
              </Link>
              .
            </p>
          </div>
        </div>
      </Section>

      {/* --- Professional setup ----------------------------------------- */}
      <Section className="border-b border-hull-800">
        <Eyebrow>Professional service</Eyebrow>
        <Heading className="text-white">Open-source agent setup</Heading>
        <Lead className="mt-4 max-w-2xl">
          A separate, one-time engagement to stand up an open-source agent setup
          in your environment. This is <strong className="text-white">not</strong>{' '}
          a WaveQA subscription and does not include one.
        </Lead>

        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <Card>
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="text-base font-semibold text-white">Setup</h3>
              <p className="text-2xl font-semibold text-white">
                $4,000
                <span className="ml-1 text-sm font-normal text-hull-400">/ setup</span>
              </p>
            </div>
            <p className="mt-2 text-sm text-hull-300">
              Estimated completion: 10 calendar days.
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-hull-300">
              {[
                'Discovery meetings',
                'Technical setup and configuration',
                'Environment setup',
                'Integration guidance',
                'Testing',
                'User training',
                'Operational guidance and handover',
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <span aria-hidden="true" className="text-surge-400">
                    ·
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 border-t border-hull-700 pt-4 text-xs text-hull-400">
              50% upfront to start; the remainder according to the agreed
              milestone and completion terms.
            </p>
          </Card>

          <Card>
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="text-base font-semibold text-white">
                Monthly support
                <span className="ml-2 rounded-full border border-hull-600 px-2 py-0.5 text-[11px] font-normal text-hull-300">
                  Optional
                </span>
              </h3>
              <p className="text-2xl font-semibold text-white">
                $2,000
                <span className="ml-1 text-sm font-normal text-hull-400">/ month</span>
              </p>
            </div>
            <p className="mt-2 text-sm text-hull-300">
              Up to 20 hours of discussion and support per month.
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-hull-300">
              {[
                'Configuration assistance',
                'Operational guidance',
                'Troubleshooting',
                'Agent usage guidance',
                'Monthly review',
                'Upgrade guidance',
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <span aria-hidden="true" className="text-surge-400">
                    ·
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 border-t border-hull-700 pt-4 text-xs text-hull-400">
              The objective is for your team to operate the agent independently.
              Support is optional and can be stopped at any time.
            </p>
          </Card>
        </div>
      </Section>

      <Section>
        <Card className="text-center">
          <Heading className="text-white">Not sure which fits?</Heading>
          <Lead className="mx-auto mt-3 max-w-xl">
            Start on the Free plan and run a verification against a real project.
            You can change plan later without losing your run history.
          </Lead>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink to="/register">Get Started</ButtonLink>
            <ButtonLink to="/contact" variant="secondary">
              Talk to us
            </ButtonLink>
          </div>
        </Card>
      </Section>
    </>
  );
}

function PlanCard({ plan }: { plan: Plan }) {
  const copy = PLAN_COPY[plan.plan];
  const featured = plan.plan === 'standard';

  return (
    <Card
      as="li"
      className={cx(
        'flex flex-col',
        featured && 'border-surge-600 ring-1 ring-surge-600/40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-white">{plan.name}</h3>
        {featured && (
          <span className="rounded-full bg-surge-500/15 px-2.5 py-0.5 text-[11px] font-semibold text-surge-300">
            Popular
          </span>
        )}
      </div>

      <p className="mt-3 text-3xl font-semibold text-white">
        {formatPrice(plan.monthlyPriceMinorUnits, plan.currency)}
        {plan.monthlyPriceMinorUnits !== null && plan.monthlyPriceMinorUnits > 0 && (
          <span className="ml-1 text-sm font-normal text-hull-400">/ month</span>
        )}
      </p>

      <ul className="mt-5 flex-1 space-y-2 text-sm text-hull-300">
        <li className="flex gap-2">
          <span aria-hidden="true" className="text-surge-400">
            ✓
          </span>
          {plan.agentLimit} QA {plan.agentLimit === 1 ? 'agent' : 'agents'}
        </li>
        <li className="flex gap-2">
          <span aria-hidden="true" className="text-surge-400">
            ✓
          </span>
          {copy.projectSetup}
        </li>
        <li className="flex gap-2">
          <span aria-hidden="true" className="text-surge-400">
            ✓
          </span>
          {plan.unlimitedHours
            ? 'Unlimited hours/month'
            : `${plan.monthlyHours} hours/month`}
        </li>
        {copy.features.map((feature) => (
          <li key={feature} className="flex gap-2">
            <span aria-hidden="true" className="text-surge-400">
              ✓
            </span>
            {feature}
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <ButtonLink
          to={plan.contactSales ? '/contact' : '/register'}
          variant={featured ? 'primary' : 'secondary'}
          className="w-full"
        >
          {plan.contactSales ? 'Contact Sales' : 'Get Started'}
        </ButtonLink>
      </div>
    </Card>
  );
}
